/**
 * Series Builder component for creating recurring events.
 * Visual RRULE builder with frequency, days, time selection.
 */

import { useState, useEffect, useMemo } from "react";
import { IconRepeat } from "../../../../components/Icons.tsx";
import { haptic } from "../../../../lib/telegram.ts";
import { getTodayDateKeyBangkok } from "../../../../lib/bangkok-time.ts";
import {
  FormField,
  TextInput,
  SelectInput,
  ToggleSwitch,
} from "./CollapsibleSection.tsx";

// Frequency types
type Frequency = "DAILY" | "WEEKLY" | "MONTHLY";

// Days of week
const DAYS_OF_WEEK = [
  { value: "MO", label: "Пн" },
  { value: "TU", label: "Вт" },
  { value: "WE", label: "Ср" },
  { value: "TH", label: "Чт" },
  { value: "FR", label: "Пт" },
  { value: "SA", label: "Сб" },
  { value: "SU", label: "Вс" },
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Ежедневно" },
  { value: "WEEKLY", label: "Еженедельно" },
  { value: "MONTHLY", label: "Ежемесячно" },
];

// Interval options
const INTERVAL_OPTIONS = [
  { value: "1", label: "Каждую" },
  { value: "2", label: "Каждую 2-ю" },
  { value: "3", label: "Каждую 3-ю" },
  { value: "4", label: "Каждую 4-ю" },
];

interface SeriesConfig {
  frequency: Frequency;
  interval: number;
  daysOfWeek: string[]; // For WEEKLY: ["MO", "WE", "FR"]
  dayOfMonth?: number; // For MONTHLY: 1-31
  startTime: string; // HH:MM
  durationMinutes: number;
  seriesStartsAt: string; // ISO date
  seriesEndsAt: string | null; // ISO date or null for infinite
  hasEndDate: boolean;
}

interface SeriesBuilderProps {
  initialRrule?: string;
  initialConfig?: Partial<SeriesConfig>;
  onChange: (rrule: string, config: SeriesConfig) => void;
}

// Parse RRULE string to config
function parseRrule(rrule: string): Partial<SeriesConfig> {
  const config: Partial<SeriesConfig> = {};
  const parts = rrule.split(";");

  for (const part of parts) {
    const [key, value] = part.split("=");
    switch (key) {
      case "FREQ":
        config.frequency = value as Frequency;
        break;
      case "INTERVAL":
        config.interval = parseInt(value, 10);
        break;
      case "BYDAY":
        config.daysOfWeek = value.split(",");
        break;
      case "BYMONTHDAY":
        config.dayOfMonth = parseInt(value, 10);
        break;
      case "BYHOUR":
        config.startTime = `${value.padStart(2, "0")}:${config.startTime?.split(":")[1] || "00"}`;
        break;
      case "BYMINUTE":
        config.startTime = `${config.startTime?.split(":")[0] || "10"}:${value.padStart(2, "0")}`;
        break;
    }
  }

  return config;
}

// Build RRULE string from config
function buildRrule(config: SeriesConfig): string {
  const parts: string[] = [];

  // Frequency
  parts.push(`FREQ=${config.frequency}`);

  // Interval
  if (config.interval > 1) {
    parts.push(`INTERVAL=${config.interval}`);
  }

  // Days of week (for WEEKLY)
  if (config.frequency === "WEEKLY" && config.daysOfWeek.length > 0) {
    parts.push(`BYDAY=${config.daysOfWeek.join(",")}`);
  }

  // Day of month (for MONTHLY)
  if (config.frequency === "MONTHLY" && config.dayOfMonth) {
    parts.push(`BYMONTHDAY=${config.dayOfMonth}`);
  }

  // Time
  const [hour, minute] = config.startTime.split(":");
  parts.push(`BYHOUR=${parseInt(hour, 10)}`);
  parts.push(`BYMINUTE=${parseInt(minute, 10)}`);

  return parts.join(";");
}

// Format recurrence description
function formatRecurrenceDescription(config: SeriesConfig): string {
  const parts: string[] = [];

  // Frequency
  switch (config.frequency) {
    case "DAILY":
      if (config.interval === 1) {
        parts.push("Каждый день");
      } else {
        parts.push(`Каждые ${config.interval} дня`);
      }
      break;
    case "WEEKLY":
      if (config.interval === 1) {
        parts.push("Каждую неделю");
      } else {
        parts.push(`Каждые ${config.interval} недели`);
      }
      if (config.daysOfWeek.length > 0) {
        const dayLabels = config.daysOfWeek
          .map((d) => DAYS_OF_WEEK.find((day) => day.value === d)?.label)
          .filter(Boolean);
        parts.push(`(${dayLabels.join(", ")})`);
      }
      break;
    case "MONTHLY":
      if (config.interval === 1) {
        parts.push("Каждый месяц");
      } else {
        parts.push(`Каждые ${config.interval} месяца`);
      }
      if (config.dayOfMonth) {
        parts.push(`${config.dayOfMonth}-го числа`);
      }
      break;
  }

  // Time
  parts.push(`в ${config.startTime}`);

  // Duration
  if (config.durationMinutes >= 60) {
    const hours = Math.floor(config.durationMinutes / 60);
    const mins = config.durationMinutes % 60;
    parts.push(`(${hours}ч${mins > 0 ? ` ${mins}м` : ""})`);
  } else {
    parts.push(`(${config.durationMinutes}м)`);
  }

  return parts.join(" ");
}

export function SeriesBuilder({
  initialRrule,
  initialConfig,
  onChange,
}: SeriesBuilderProps) {
  // Default config
  const defaultConfig: SeriesConfig = {
    frequency: "WEEKLY",
    interval: 1,
    daysOfWeek: ["SU"],
    dayOfMonth: 1,
    startTime: "10:00",
    durationMinutes: 120,
    seriesStartsAt: getTodayDateKeyBangkok(),
    seriesEndsAt: null,
    hasEndDate: false,
  };

  // Parse initial RRULE if provided
  const parsedConfig = initialRrule ? parseRrule(initialRrule) : {};

  // Merge configs
  const [config, setConfig] = useState<SeriesConfig>({
    ...defaultConfig,
    ...parsedConfig,
    ...initialConfig,
  });

  // Update parent on config change
  useEffect(() => {
    const rrule = buildRrule(config);
    onChange(rrule, config);
  }, [config, onChange]);

  // Update config field
  const updateConfig = <K extends keyof SeriesConfig>(
    key: K,
    value: SeriesConfig[K],
  ) => {
    haptic("light");
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  // Toggle day of week
  const toggleDay = (day: string) => {
    haptic("light");
    setConfig((prev) => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter((d) => d !== day)
        : [...prev.daysOfWeek, day],
    }));
  };

  // Recurrence description
  const description = useMemo(() => formatRecurrenceDescription(config), [config]);

  return (
    <div className="space-y-4">
      {/* Preview */}
      <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center text-slate-500">
          <IconRepeat className="w-5 h-5" />
        </div>
        <div>
          <p className="text-[14px] font-medium text-slate-900">{description}</p>
          <p className="text-[12px] text-slate-500">
            {config.hasEndDate && config.seriesEndsAt
              ? `До ${new Date(config.seriesEndsAt).toLocaleDateString("ru-RU")}`
              : "Без даты окончания"}
          </p>
        </div>
      </div>

      {/* Frequency */}
      <FormField label="Частота">
        <SelectInput
          value={config.frequency}
          onChange={(v) => updateConfig("frequency", v as Frequency)}
          options={FREQUENCY_OPTIONS}
        />
      </FormField>

      {/* Interval */}
      <FormField label="Интервал">
        <SelectInput
          value={String(config.interval)}
          onChange={(v) => updateConfig("interval", parseInt(v, 10))}
          options={INTERVAL_OPTIONS}
        />
      </FormField>

      {/* Days of week (for WEEKLY) */}
      {config.frequency === "WEEKLY" && (
        <FormField label="Дни недели">
          <div className="flex gap-1.5">
            {DAYS_OF_WEEK.map((day) => {
              const isSelected = config.daysOfWeek.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`flex-1 py-2 rounded-lg text-[13px] font-medium transition-colors ${
                    isSelected
                      ? "bg-[var(--color-accent)] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </FormField>
      )}

      {/* Day of month (for MONTHLY) */}
      {config.frequency === "MONTHLY" && (
        <FormField label="День месяца">
          <TextInput
            type="number"
            value={String(config.dayOfMonth || 1)}
            onChange={(v) => updateConfig("dayOfMonth", parseInt(v, 10) || 1)}
            min={1}
            max={31}
          />
        </FormField>
      )}

      {/* Time */}
      <FormField label="Время начала">
        <input
          type="time"
          value={config.startTime}
          onChange={(e) => updateConfig("startTime", e.target.value)}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent text-slate-900 text-[15px]"
        />
      </FormField>

      {/* Duration */}
      <FormField label="Длительность (минуты)">
        <TextInput
          type="number"
          value={String(config.durationMinutes)}
          onChange={(v) => updateConfig("durationMinutes", parseInt(v, 10) || 60)}
          min={15}
        />
      </FormField>

      {/* Series start date */}
      <FormField label="Начало серии">
        <input
          type="date"
          value={config.seriesStartsAt.split("T")[0]}
          onChange={(e) => updateConfig("seriesStartsAt", e.target.value)}
          className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent text-slate-900 text-[15px]"
        />
      </FormField>

      {/* Has end date toggle */}
      <ToggleSwitch
        label="Дата окончания"
        description="Установить дату окончания серии"
        checked={config.hasEndDate}
        onChange={(v) => updateConfig("hasEndDate", v)}
        color="blue"
      />

      {/* Series end date */}
      {config.hasEndDate && (
        <FormField label="Окончание серии">
          <input
            type="date"
            value={config.seriesEndsAt?.split("T")[0] || ""}
            onChange={(e) => updateConfig("seriesEndsAt", e.target.value || null)}
            className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent text-slate-900 text-[15px]"
          />
        </FormField>
      )}
    </div>
  );
}

export { buildRrule, parseRrule, formatRecurrenceDescription };
export type { SeriesConfig, Frequency };
